type TPdfKeepTogetherRange = {
  top: number;
  bottom: number;
};

export const collectPdfKeepTogetherRanges = (container: HTMLElement, pageHeight: number): TPdfKeepTogetherRange[] => {
  const containerTop = container.getBoundingClientRect().top;
  const ranges: TPdfKeepTogetherRange[] = [];
  const elementRects = new WeakMap<Element, DOMRect>();

  const getRect = (element: Element): DOMRect => {
    const cachedRect = elementRects.get(element);
    if (cachedRect) return cachedRect;

    const rect = element.getBoundingClientRect();
    elementRects.set(element, rect);
    return rect;
  };

  const addRange = (start: Element, end: Element = start) => {
    ranges.push({
      top: getRect(start).top - containerTop,
      bottom: getRect(end).bottom - containerTop,
    });
  };

  const visit = (element: Element) => {
    const { height } = getRect(element);
    if (!height) return;

    if (element.matches('table')) {
      const table = element as HTMLTableElement;
      const firstRow = table.tBodies.item(0)?.rows.item(0);
      if (firstRow) {
        addRange(table, firstRow);
      }

      Array.from(table.rows).forEach((row) => {
        const sectionRows = (row.parentElement as HTMLTableSectionElement).rows;
        const remainingRows = sectionRows.length - row.sectionRowIndex;
        const rowSpan = Math.max(
          1,
          ...Array.from(row.cells, (cell) => (cell.rowSpan === 0 ? remainingRows : cell.rowSpan)),
        );
        const lastRow = sectionRows.item(row.sectionRowIndex + Math.min(rowSpan, remainingRows) - 1)!;
        addRange(row, lastRow);
      });
      return;
    }

    const table = element.querySelector('table');
    if (!table && height <= pageHeight) {
      addRange(element);
      return;
    }

    if (table && element.hasAttribute('data-pdf-table-section')) {
      const firstRow = table.tBodies.item(0)?.rows.item(0);
      if (firstRow) addRange(element, firstRow);
    }

    Array.from(element.children).forEach(visit);
  };

  Array.from(container.children).forEach(visit);
  return ranges;
};

const KEEP_TOGETHER_OVERLAP_EPSILON = 2;

const mergeKeepTogetherRanges = (ranges: TPdfKeepTogetherRange[], pageHeight: number): TPdfKeepTogetherRange[] => {
  const groups: TPdfKeepTogetherRange[] = [];
  ranges
    .filter((range) => range.bottom - range.top <= pageHeight)
    .map((range) => ({ top: Math.floor(range.top), bottom: Math.ceil(range.bottom) }))
    .filter((range) => range.bottom > range.top)
    .sort((leftRange, rightRange) => leftRange.top - rightRange.top)
    .forEach((range) => {
      const previous = groups[groups.length - 1];

      if (previous && range.top < previous.bottom - KEEP_TOGETHER_OVERLAP_EPSILON) {
        const merged = {
          top: previous.top,
          bottom: Math.max(previous.bottom, range.bottom),
        };

        if (merged.bottom - merged.top <= pageHeight) {
          groups.splice(groups.length - 1, 1, merged);
          return;
        }
      }

      groups.push(range);
    });

  return groups;
};

export const getPdfPageSlices = (
  contentHeight: number,
  pageHeight: number,
  ranges: TPdfKeepTogetherRange[],
): TPdfKeepTogetherRange[] => {
  const boundedRanges = ranges
    .filter((range) => range.top < contentHeight)
    .map((range) => ({ top: range.top, bottom: Math.min(range.bottom, contentHeight) }));

  const groups = mergeKeepTogetherRanges(boundedRanges, pageHeight);
  const pages: TPdfKeepTogetherRange[] = [];
  const groupIterator = groups.values();
  let currentGroup: TPdfKeepTogetherRange | undefined = groupIterator.next().value;
  let top = 0;
  while (top < contentHeight) {
    const limit = Math.min(top + pageHeight, contentHeight);
    while (currentGroup && (currentGroup.bottom <= limit || currentGroup.bottom - currentGroup.top > pageHeight)) {
      currentGroup = groupIterator.next().value;
    }
    const crossing = currentGroup && currentGroup.top < limit ? currentGroup : undefined;
    const breakTop = crossing ? Math.floor(crossing.top) : limit;
    const bottom = breakTop > top ? breakTop : limit;
    pages.push({ top, bottom });
    top = bottom;
  }
  return pages;
};
